import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, FileText, LayoutTemplate, Rocket } from 'lucide-react';
import { useParallaxRef } from '@/utils/parallax';
import { useRevealOnce } from '@/hooks/useRevealOnce';
import { Button } from '@/components/ui/button';
import {
  Timeline,
  TimelineItem,
  TimelinePoint,
  TimelineContent,
  TimelineTime,
  TimelineTitle,
  TimelineBody,
} from '@/components/lib/flowbite/Timeline';

interface ProcessStep {
  step: string;
  title: string;
  description: string;
  icon: React.ReactNode;
}

const processSteps: ProcessStep[] = [
  {
    step: 'Step 1',
    title: 'Free 1-hour consult',
    description:
      "We review your domain, niche, current site (if any), and goals. You'll leave with a recommended path: Starter, Basic, Pro, or a custom option if needed.",
    icon: <Calendar className="w-3 h-3 text-white" />,
  },
  {
    step: 'Step 2',
    title: 'Plan & proposal',
    description:
      'I map out scope, deliverables, timeline, and investment. We make sure it fits your priorities and capacity before we lock anything in.',
    icon: <FileText className="w-3 h-3 text-white" />,
  },
  {
    step: 'Step 3',
    title: 'Build, review & refine',
    description:
      "We work through brand, site, and systems in clear phases. You see concrete milestones, give feedback at set checkpoints, and always know what's happening next.",
    icon: <LayoutTemplate className="w-3 h-3 text-white" />,
  },
  {
    step: 'Step 4',
    title: 'Launch & support',
    description:
      'We connect everything, launch your new presence, and walk through your 30–60 day launch plan so you know how to drive traffic, handle leads, and make the most of your new system.',
    icon: <Rocket className="w-3 h-3 text-white" />,
  },
];

export default function ServicesProcessSection() {
  // Parallax for subtitle
  const subtitleRef = useParallaxRef({
    speed: -0.12,
    axis: 'y',
    maxTranslate: 10,
  });

  // Parallax for H2 (subtle)
  const titleRef = useParallaxRef({
    speed: -0.06,
    axis: 'y',
    maxTranslate: 6,
  });

  // Reveal hook for section entrance
  const sectionReveal = useRevealOnce({ amount: 0.2 });

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const timelineItemVariants = {
    hidden: { opacity: 0, y: 30 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: 'easeOut' as const },
    },
  };

  const handleScrollToPricing = () => {
    const pricingElement = document.getElementById('packages') || document.getElementById('pricing');
    if (pricingElement) {
      pricingElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      {/* Main Section */}
      <section className="relative w-full bg-black text-slate-100 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              ref={sectionReveal.ref}
              variants={containerVariants}
              initial="hidden"
              animate={sectionReveal.controls}
            >
              {/* Heading Section */}
              <div className="text-center mb-12 md:mb-16">
                <h2
                  ref={titleRef as any}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-slate-50 mb-6"
                >
                  How we'll work together
                </h2>
                <p
                  ref={subtitleRef as any}
                  className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl mx-auto"
                >
                  No mystery, no disappearing acts. Here's what the process looks like from first call to launch.
                </p>
              </div>

              {/* Timeline */}
              <div className="mb-12 md:mb-16">
                <Timeline className="max-w-2xl mx-auto">
                  {processSteps.map((step, index) => (
                    <motion.div key={index} variants={timelineItemVariants}>
                      <TimelineItem>
                        <TimelinePoint icon={step.icon} />
                        <TimelineContent>
                          <TimelineTime>{step.step}</TimelineTime>
                          <TimelineTitle>{step.title}</TimelineTitle>
                          <TimelineBody>{step.description}</TimelineBody>
                        </TimelineContent>
                      </TimelineItem>
                    </motion.div>
                  ))}
                </Timeline>
              </div>

              {/* CTA Section */}
              <motion.div
                variants={timelineItemVariants}
                className="text-center space-y-4"
              >
                <p className="text-slate-300 text-sm md:text-base mb-4">
                  Ready to see what each level includes?
                </p>
                <Button
                  onClick={handleScrollToPricing}
                  className="bg-white text-slate-900 hover:bg-slate-100 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200 px-8 py-6 text-base font-medium"
                >
                  See packages & pricing
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

    </>
  );
}

